/* eslint-disable no-unused-vars */
import { ViewerSoftwareOptions, SoftwareOptionsForCompany } from '../constants/enums.constants';
import { TIMELAPSE, MODEL_COMPARE, IOSIM_PLUS, DENTAL_XRAI, NIRI, VISIT_REPORT } from '../constants/tools.constants';
import { utils } from '../utils';
import { scannerVersionManager } from '../scanner-version-manager/scanner-version-manager';
import {
  getAvailbilityForPluginOrFeature,
  getIsIntraOralCameraAndNiriEnabled,
  getIsIntraOralCameraEnabled,
  getIsNGNiriEnabledForLuminaPro,
  getIsNGNiriHidden,
  shouldEnableEvx,
} from './feature-availability-utils';

const getAllAvaliableFeaturesIds = () => {
  const avaliableFeaturesArray = [];
  const isModelCompareEnabled = isSideBySideCompareEnabled();
  const isIOSimPlusEnabled = getIsIOSimPlusEnabled();
  const isTimeLapseEnabled = getIsTimeLapseEnabled();
  const isDentalXraiEnabled = getIsDentalXraiEnabled();
  const isNiriEnabled = !getIsNGNiriHidden();
  const isVisitReport = getIsVisitReportEnabled();
  isDentalXraiEnabled && avaliableFeaturesArray.push(DENTAL_XRAI.id);
  isModelCompareEnabled && avaliableFeaturesArray.push(MODEL_COMPARE.id);
  isIOSimPlusEnabled && avaliableFeaturesArray.push(IOSIM_PLUS.id);
  isTimeLapseEnabled && avaliableFeaturesArray.push(TIMELAPSE.id);
  isNiriEnabled && avaliableFeaturesArray.push(NIRI.id);
  isVisitReport && avaliableFeaturesArray.push(VISIT_REPORT.id);
  return avaliableFeaturesArray;
};

const isSideBySideCompareEnabled = () => {
  const resolveFunction = (isFeatureFlagEnabled, isCompanySwoEnabled, isScannerSwoEnabled) => {
    return isScannerSwoEnabled;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: ['SideBySideCompare'],
    scannerSwo: ViewerSoftwareOptions.SideBySideCompare,
    resolveFunction,
  });
};

const getIsTimeLapseEnabled = () => {
  const isEup = utils.isEupHostEnv();
  const isAOHS = utils.isAOHSEnv();
  if (isEup || isAOHS) return false;

  const resolveFunction = (isFeatureFlagEnabled, isCompanySwoEnabled, isScannerSwoEnabled) => {
    return true;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: [],
    companySwo: null,
    resolveFunction,
  });
};

const getIsIOSimPlusEnabled = () => {
  // TODO implement
  const resolveFunction = (isFeatureFlagEnabled, isCompanySwoEnabled, isScannerSwoEnabled) => {
    const isIOSimPlusEnabled = false;
    const iosimProps = {
      expectedDuration: null,
      simulationStatus: null,
      startSimulationTime: null,
    };
    return true;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: [],
    scannerSwo: null,
    resolveFunction,
  });
};

const getIsDentalXraiEnabled = () => {
  const resolveFunction = (isFeatureFlagEnabled, isCompanySwoEnabled, isScannerSwoEnabled) => {
    return isFeatureFlagEnabled;
  };

  const dentalXRGAFF = getAvailbilityForPluginOrFeature({
    featureFlagNames: ['cswo_DentalXRGA'],
    scannerSwo: null,
    resolveFunction,
  });

  const dentalXRDisplayFF = getAvailbilityForPluginOrFeature({
    featureFlagNames: ['cswo_DentalXRDisplay'],
    scannerSwo: null,
    resolveFunction,
  });

  return dentalXRGAFF || dentalXRDisplayFF;
};

const getViewer360GuidedTour = () => {
  const resolveFunction = (isFeatureFlagEnabled, isCompanySwoEnabled, isScannerSwoEnabled) => {
    return isFeatureFlagEnabled;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: ['Viewer360GuidedTour'],
    scannerSwo: null,
    resolveFunction,
  });
};

// Align orientation SWO and FF were changed to be aligned to the behavior of 2D compare feature and thus it uses it FF and SWO (TR-15710)
const getViewer360Align2DImages = () => {
  const resolveFunction = (isFeatureFlagEnabled, isCompanySwoEnabled, isScannerSwoEnabled) => {
    const is360 = utils.getIs360HubEnabled();
    return is360 && isFeatureFlagEnabled && isScannerSwoEnabled;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: ['Viewer360HistoricalComparison2DComapre'],
    scannerSwo: ViewerSoftwareOptions.Viewer360HistoricalComparisonManual2DComapre,
    resolveFunction,
  });
};

const getIsWasmEnabled = () => {
  const resolveFunction = (isFeatureFlagEnabled, isCompanySwoEnabled, isScannerSwoEnabled) => {
    return isFeatureFlagEnabled;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: ['UseWasmInWeb3DTool'],
    scannerSwo: null,
    resolveFunction,
  });
};

const getNGNiriEnabled = () => {
  const resolveFunction = (isFeatureFlagEnabled, companySwo, scannerSwo) => {
    const isScanner = utils.isScannerHostEnv();
    if (isScanner) {
      const scannerCurrentVersion = utils.getScannerPackageVersion();
      const isScannerOfVersion = scannerVersionManager.checkScannerPackageVersions(scannerCurrentVersion, [
        { major: 4 },
      ]);
      return (isScannerOfVersion && scannerSwo) || getIsIntraOralCameraAndNiriEnabled();
    } else {
      const isEvxEnabled = shouldEnableEvx();
      return scannerSwo || isEvxEnabled;
    }
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: [],
    scannerSwo: ViewerSoftwareOptions.NGNIRI,
    resolveFunction,
  });
};

const getNGIocEnabled = () => {
  const resolveFunction = (isFeatureFlagEnabled, companySwo, scannerSwo) => {
    const isScanner = utils.isScannerHostEnv();
    if (isScanner) {
      const scannerCurrentVersion = utils.getScannerPackageVersion();
      const isScannerOfVersion = scannerVersionManager.checkScannerPackageVersions(scannerCurrentVersion, [
        { major: 3 },
        { major: 4 },
      ]);
      return (
        (isScannerOfVersion && scannerSwo) ||
        getIsIntraOralCameraEnabled() ||
        getIsIntraOralCameraAndNiriEnabled() ||
        getIsNGNiriEnabledForLuminaPro()
      );
    } else {
      return true;
    }
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: [],
    scannerSwo: ViewerSoftwareOptions.NGIOC,
    resolveFunction,
  });
};

const getIsReviewToolInCompareModeEnabled = () => {
  const resolveFunction = (isFeatureFlagEnabled, isCompanySwoEnabled, isScannerSwoEnabled) => {
    return isCompanySwoEnabled || isScannerSwoEnabled;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: [],
    companySwo: null,
    scannerSwo: ViewerSoftwareOptions.Viewer360HistoricalComparisonManual2DComapre,
    resolveFunction,
  });
};

const getIsRulesMatrixEnabled = () => {
  const isAOHS = utils.isAOHSEnv();
  const resolveFunction = (isFeatureFlagEnabled, companySwo, scannerSwo) => {
    const isScanner = utils.isScannerHostEnv();
    return !isScanner && isFeatureFlagEnabled;
  };

  return (
    isAOHS ||
    getAvailbilityForPluginOrFeature({
      featureFlagNames: ['Web3dToolUseRulesMatrix'],
      scannerSwo: null,
      resolveFunction,
    })
  );
};

const getIsAssetManagerEnabled = () => {
  const resolveFunction = (isFeatureFlagEnabled, companySwo, scannerSwo) => {
    return isFeatureFlagEnabled;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: ['UseAssetManagerInWeb3DTool'],
    scannerSwo: null,
    resolveFunction,
  });
};

const getIsAOHSGalleryViewEnabled = () => {
  const resolveFunction = (isFeatureFlagEnabled, companySwo, scannerSwo) => {
    return isFeatureFlagEnabled;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: ['AlignOralHealthSuiteGalleryView'],
    scannerSwo: null,
    resolveFunction,
  });
};

const getIsEnableNGNiriImageSelectionAlgo = () => {
  const resolveFunction = (isFeatureFlagEnabled, companySwo, scannerSwo) => {
    return isFeatureFlagEnabled;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: ['EnableNGNiriImageSelectionAlgo'],
    scannerSwo: null,
    resolveFunction,
  });
};

const getIsVisitReportEnabledGA = () => {
  const resolveFunction = (isFeatureFlagEnabled, isCompanySwoEnabled, isScannerSwoEnabled) => {
    return isFeatureFlagEnabled || isCompanySwoEnabled;
  };
  return getAvailbilityForPluginOrFeature({
    featureFlagNames: ['ShareScanReportByQR_GA'],
    companySwo: SoftwareOptionsForCompany.VisitReportLmr,
    scannerSwo: null,
    resolveFunction,
  });
};

const getIsVisitReportFeatureFlagEnabled = () => {
  const resolveFunction = (isFeatureFlagEnabled, isCompanySwoEnabled, isScannerSwoEnabled) => {
    return isFeatureFlagEnabled;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: ['ScanReportInsideAOHS'],
    companySwo: null,
    scannerSwo: null,
    resolveFunction,
  });
};

const getIsChangeComparedScanButtonEnabled = () => {
  const resolveFunction = (isFeatureFlagEnabled, companySwo, scannerSwo) => {
    return isFeatureFlagEnabled;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: ['ChangeComparedScanButton'],
    scannerSwo: null,
    resolveFunction,
  });
};

const getIsPinIocNiriRotationButtonEnabled = () => {
  const isAOHS = utils.isAOHSEnv();

  const resolveFunction = (isFeatureFlagEnabled, companySwo, scannerSwo) => {
    return isFeatureFlagEnabled;
  };

  return (
    isAOHS &&
    getAvailbilityForPluginOrFeature({
      featureFlagNames: ['cswo_Pin_IOC_NIRI_Rotation_Button'],
      scannerSwo: null,
      resolveFunction,
    })
  );
};

const getIsVisitReportEnabled = () => {
  const isDemoModeSession = utils.isDemoMode();

  if (!isDemoModeSession) {
    return getIsVisitReportFeatureFlagEnabled() && getIsVisitReportEnabledGA();
  }

  const visitReportDemoModeSetings = utils.visitReportDemoModeSettings();
  const scannerSystemType = utils.getScannerSystemType();

  if (visitReportDemoModeSetings && scannerSystemType) {
    const demoModePatientId = visitReportDemoModeSetings[scannerSystemType.toLowerCase()];
    const currentPatientId = utils.getPatientGuid();

    return demoModePatientId && demoModePatientId === currentPatientId;
  }
  return false;
};

const getIsAssetsSdkCachingEnabled = () => {
  const resolveFunction = (isFeatureFlagEnabled, companySwo, scannerSwo) => {
    return isFeatureFlagEnabled;
  };

  return getAvailbilityForPluginOrFeature({
    featureFlagNames: ['UseAssetsSdkCaching'],
    scannerSwo: null,
    resolveFunction,
  });
};

export const featureAvaliability = {
  isSideBySideCompareEnabled,
  getIsTimeLapseEnabled,
  getIsDentalXraiEnabled,
  getAllAvaliableFeaturesIds,
  getViewer360GuidedTour,
  getViewer360Align2DImages,
  getIsWasmEnabled,
  getNGNiriEnabled,
  getNGIocEnabled,
  getIsReviewToolInCompareModeEnabled,
  getIsRulesMatrixEnabled,
  getIsAssetManagerEnabled,
  getIsIOSimPlusEnabled,
  getIsAOHSGalleryViewEnabled,
  getIsEnableNGNiriImageSelectionAlgo,
  getIsVisitReportEnabled,
  getIsVisitReportEnabledGA,
  getIsChangeComparedScanButtonEnabled,
  getIsPinIocNiriRotationButtonEnabled,
  getIsAssetsSdkCachingEnabled,
};
